import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Select, DatePicker } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePWC301Query } from './hooks/usePWC301Query';
import type { SCDetailRow, QueryParams } from '../../services/pwc301Api';
import './PWC301Form.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const CONTRACT_TYPE_OPTIONS = [
  { value: 3, label: '全部' },
  { value: 1, label: '內銷' },
  { value: 2, label: '外銷' },
];

const CONTRACT_STATUS_OPTIONS = [
  { value: 3, label: '全部' },
  { value: 1, label: '僅PI' },
  { value: 2, label: '非PI' },
];

const GROUP_BY_OPTIONS = [
  { value: 'doc', label: '依單號' },
  { value: 'customer', label: '依客戶' },
  { value: 'dept', label: '依部門' },
  { value: 'salesperson', label: '依業務員' },
];

const PWC301Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePWC301Query();

  const [scNoFrom, setScNoFrom] = useState('');
  const [scNoTo, setScNoTo] = useState('');
  const [scDateRange, setScDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [customerFrom, setCustomerFrom] = useState('');
  const [customerTo, setCustomerTo] = useState('');
  const [productFrom, setProductFrom] = useState('');
  const [productTo, setProductTo] = useState('');
  const [apprDateRange, setApprDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [salesCustomerFrom, setSalesCustomerFrom] = useState('');
  const [salesCustomerTo, setSalesCustomerTo] = useState('');
  const [contractType, setContractType] = useState<number>(3);
  const [contractStatus, setContractStatus] = useState<number>(3);
  const [groupBy, setGroupBy] = useState<string>('doc');

  const handleQuery = () => {
    const params: QueryParams = {
      scNoFrom: scNoFrom.trim() || undefined,
      scNoTo: scNoTo.trim() || undefined,
      scDateFrom: scDateRange[0]?.format('YYYY-MM-DD') || undefined,
      scDateTo: scDateRange[1]?.format('YYYY-MM-DD') || undefined,
      customerFrom: customerFrom.trim() || undefined,
      customerTo: customerTo.trim() || undefined,
      productFrom: productFrom.trim() || undefined,
      productTo: productTo.trim() || undefined,
      apprDateFrom: apprDateRange[0]?.format('YYYY-MM-DD') || undefined,
      apprDateTo: apprDateRange[1]?.format('YYYY-MM-DD') || undefined,
      salesCustomerFrom: salesCustomerFrom.trim() || undefined,
      salesCustomerTo: salesCustomerTo.trim() || undefined,
      contractType,
      contractStatus,
      groupBy,
    };
    runQuery(params);
  };

  const handleReset = () => {
    setScNoFrom(''); setScNoTo('');
    setScDateRange([null, null]);
    setCustomerFrom(''); setCustomerTo('');
    setProductFrom(''); setProductTo('');
    setApprDateRange([null, null]);
    setSalesCustomerFrom(''); setSalesCustomerTo('');
    setContractType(3);
    setContractStatus(3);
    setGroupBy('doc');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>S/C明細表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:8mm}}</style></head><body>
<h2>S/C明細表</h2>
<table><thead><tr><th>SC單號</th><th>SC日期</th><th>客戶</th><th>業務員</th><th>品號</th><th>品名</th><th>數量</th><th>單位</th><th>單價</th><th>金額</th><th>交期</th><th>核准日</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FC110_01}</td><td>${r.FC110_11?.substring(0,10)||''}</td><td>${r.FC110_12} ${r.customer_name}</td><td>${r.FC110_13} ${r.salesperson_name}</td><td>${r.FC111_10}${r.FC111_11}</td><td></td><td class="right">${r.FC111_13}</td><td>${r.FC111_14}</td><td class="right">${r.FC111_15}</td><td class="right">${r.FC111_16}</td><td>${r.FC111_12?.substring(0,10)||''}</td><td>${r.FC110_521?.substring(0,10)||''}</td></tr>`).join('')}
</tbody></table></body></html>`);
    win.document.close(); win.focus(); win.print();
  };

  const columns: ColumnsType<SCDetailRow> = [
    { title: 'SC單號', dataIndex: 'FC110_01', key: 'FC110_01', width: 110, fixed: 'left' as const },
    { title: 'SC日期', dataIndex: 'FC110_11', key: 'FC110_11', width: 100, render: (v: string) => v?.substring(0,10)||'' },
    { title: '客戶代碼', dataIndex: 'FC110_12', key: 'FC110_12', width: 90 },
    { title: '客戶名稱', dataIndex: 'customer_name', key: 'customer_name', width: 140, ellipsis: true },
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 100, ellipsis: true },
    { title: '業務員', dataIndex: 'salesperson_name', key: 'salesperson_name', width: 100, ellipsis: true },
    { title: '品號', key: 'product_code', width: 130, render: (_: unknown, r: SCDetailRow) => `${r.FC111_10}${r.FC111_11}` },
    { title: '行', dataIndex: 'FC111_02', key: 'FC111_02', width: 50, align: 'right' as const },
    { title: '數量', dataIndex: 'FC111_13', key: 'FC111_13', width: 80, align: 'right' as const },
    { title: '單位', dataIndex: 'FC111_14', key: 'FC111_14', width: 60 },
    { title: '單價', dataIndex: 'FC111_15', key: 'FC111_15', width: 90, align: 'right' as const },
    { title: '金額', dataIndex: 'FC111_16', key: 'FC111_16', width: 100, align: 'right' as const },
    { title: '交期', dataIndex: 'FC111_12', key: 'FC111_12', width: 100, render: (v: string) => v?.substring(0,10)||'' },
    { title: '核准日', dataIndex: 'FC110_521', key: 'FC110_521', width: 100, render: (v: string) => v?.substring(0,10)||'' },
    { title: '顏色(中)', dataIndex: 'FC115_12', key: 'FC115_12', width: 100, ellipsis: true },
    { title: '色量', dataIndex: 'FC115_14', key: 'FC115_14', width: 70, align: 'right' as const },
  ];

  return (
    <PermissionGuard module={Modules.PWC301} action="query">
      <div className="pwc301-container">
        <div className="pwc301-header">
          <h2>S/C明細表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space wrap size="middle">
              <div>
                <label className="pwc301-label">SC單號起</label>
                <Input value={scNoFrom} onChange={e => setScNoFrom(e.target.value)}
                  placeholder="起始單號" style={{ width: 130 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">SC單號迄</label>
                <Input value={scNoTo} onChange={e => setScNoTo(e.target.value)}
                  placeholder="結束單號" style={{ width: 130 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">SC日期範圍</label>
                <RangePicker
                  value={scDateRange}
                  onChange={v => setScDateRange(v ? [v[0], v[1]] : [null, null])}
                  style={{ width: 240 }} />
              </div>
              <div>
                <label className="pwc301-label">核准日期範圍</label>
                <RangePicker
                  value={apprDateRange}
                  onChange={v => setApprDateRange(v ? [v[0], v[1]] : [null, null])}
                  style={{ width: 240 }} />
              </div>
            </Space>
            <Space wrap size="middle">
              <div>
                <label className="pwc301-label">客戶代碼起</label>
                <Input value={customerFrom} onChange={e => setCustomerFrom(e.target.value)}
                  placeholder="起始" style={{ width: 110 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">客戶代碼迄</label>
                <Input value={customerTo} onChange={e => setCustomerTo(e.target.value)}
                  placeholder="結束" style={{ width: 110 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">業績客戶起</label>
                <Input value={salesCustomerFrom} onChange={e => setSalesCustomerFrom(e.target.value)}
                  placeholder="起始" style={{ width: 110 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">業績客戶迄</label>
                <Input value={salesCustomerTo} onChange={e => setSalesCustomerTo(e.target.value)}
                  placeholder="結束" style={{ width: 110 }} allowClear />
              </div>
            </Space>
            <Space wrap size="middle">
              <div>
                <label className="pwc301-label">品號起</label>
                <Input value={productFrom} onChange={e => setProductFrom(e.target.value)}
                  placeholder="起始" style={{ width: 130 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">品號迄</label>
                <Input value={productTo} onChange={e => setProductTo(e.target.value)}
                  placeholder="結束" style={{ width: 130 }} allowClear />
              </div>
              <div>
                <label className="pwc301-label">合約類別</label>
                <Select value={contractType} onChange={setContractType} style={{ width: 100 }}>
                  {CONTRACT_TYPE_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
              </div>
              <div>
                <label className="pwc301-label">合約狀態</label>
                <Select value={contractStatus} onChange={setContractStatus} style={{ width: 100 }}>
                  {CONTRACT_STATUS_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
              </div>
              <div>
                <label className="pwc301-label">排序分組</label>
                <Select value={groupBy} onChange={setGroupBy} style={{ width: 110 }}>
                  {GROUP_BY_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                  <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
                </Space>
              </div>
            </Space>
          </Space>
        </Card>

        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <Table<SCDetailRow>
            columns={columns}
            dataSource={data}
            rowKey={(r, i) => `${r.FC110_01}-${r.FC111_02}-${r.FC115_03}-${i}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1400 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PWC301Form;
